<template>
  <div class="followup-timeline">
    <div v-if="followUps && followUps.length > 0">
      <div class="followup-history">
        <div class="timeline">
          <div 
            v-for="(followUp, index) in sortedFollowUps" 
            :key="index"
            class="timeline-item"
          >
            <div class="timeline-marker">
              <div class="timeline-dot"></div>
              <div v-if="index < sortedFollowUps.length - 1" class="timeline-line"></div>
            </div>
            
            <div class="timeline-content">
              <div class="followup-header">
                <div class="followup-meta">
                  <span class="followup-date">
                    <i class="fas fa-calendar-alt"></i>
                    {{ formatDateTime(followUp.date) }}
                  </span>
                  <span class="followup-user">
                    <i class="fas fa-user"></i>
                    {{ followUp.user }}
                  </span>
                </div>
                
                <div v-if="followUp.previousStatus && followUp.newStatus && followUp.previousStatus !== followUp.newStatus" class="status-change">
                  <span class="status-badge previous">{{ getStatusText(followUp.previousStatus) }}</span>
                  <i class="fas fa-arrow-right mx-2"></i>
                  <span class="status-badge new">{{ getStatusText(followUp.newStatus) }}</span>
                </div>
              </div>
              
              <div class="followup-content">
                <div class="followup-note">
                  <i class="fas fa-comment-alt"></i>
                  <span>{{ followUp.note || 'No notes provided' }}</span>
                </div>
                
                <div v-if="followUp.nextFollowUpDate" class="next-followup">
                  <i class="fas fa-clock"></i>
                  <span>Next follow-up: {{ formatDateTime(followUp.nextFollowUpDate) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="no-followups">
      <div class="text-center text-muted py-4">
        <i class="fas fa-history fa-2x mb-3"></i>
        <h6>No Past Follow-ups</h6>
        <p>This quote hasn't had any follow-up activities yet.</p>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'FollowUpTimeline',
  props: {
    followUps: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    sortedFollowUps() {
      if (!this.followUps || this.followUps.length === 0) return []
      
      return [...this.followUps].sort((a, b) => {
        return moment(b.date).valueOf() - moment(a.date).valueOf()
      })
    }
  },
  methods: {
    formatDateTime(date) {
      if (!date) return ''
      return moment(date).format('MMM D, YYYY [at] h:mm A')
    },
    getStatusText(status) {
      const statusMap = {
        'IN_PROGRESS': 'In Progress',
        'NO_FOLLOW_UP': 'No Follow-up',
        'CONVERTED': 'Converted',
        'LOST': 'Lost'
      }
      return statusMap[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
.followup-timeline {
  .followup-history {
    max-height: 450px;
    overflow-y: auto;
    padding: 0.5rem 0;
  }

  .timeline {
    position: relative;
    padding-left: 2rem;
  }

  .timeline-item {
    position: relative;
    margin-bottom: 2rem;
  }

  .timeline-item:last-child {
    margin-bottom: 0;
  }

  .timeline-marker {
    position: absolute;
    left: -2rem;
    top: 0.25rem;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .timeline-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #007bff;
    border: 3px solid #ffffff;
    box-shadow: 0 0 0 2px #007bff;
    z-index: 2;
  }

  .timeline-line {
    width: 2px;
    height: 2rem;
    background: #e9ecef;
    margin-top: 0.5rem;
  }

  .timeline-content {
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    position: relative;
    margin-left: 0;
  }

  .timeline-content:before {
    content: '';
    position: absolute;
    left: -8px;
    top: 1rem;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #e9ecef;
  }

  .timeline-content:after {
    content: '';
    position: absolute;
    left: -7px;
    top: 1rem;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #ffffff;
  }

  .followup-header {
    margin-bottom: 0.75rem;
  }

  .followup-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .followup-date, .followup-user {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.875rem;
    color: #6c757d;
  }

  .followup-date {
    font-weight: 600;
    color: #495057;
  }

  .followup-date i, .followup-user i {
    color: #007bff;
    font-size: 0.75rem;
  }

  .status-change {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 3px solid #007bff;
  }

  .status-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .status-badge.previous {
    background: #f8d7da;
    color: #721c24;
  }

  .status-badge.new {
    background: #d1edff;
    color: #0c5460;
  }

  .status-change i {
    color: #6c757d;
  }

  .followup-note, .next-followup {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .followup-note i, .next-followup i {
    color: #007bff;
    margin-top: 0.125rem;
    flex-shrink: 0;
    font-size: 0.875rem;
  }

  .followup-note span, .next-followup span {
    flex: 1;
  }

  .followup-note {
    color: #495057;
    font-weight: 500;
  }

  .followup-note span {
    line-height: 1.4;
  }

  .next-followup {
    font-size: 0.875rem;
    color: #6c757d;
    background: #f8f9fa;
    padding: 0.5rem;
    border-radius: 4px;
    margin-top: 0.5rem;
    border-left: 3px solid #28a745;
  }

  .next-followup i {
    color: #28a745 !important;
  }

  .no-followups {
    i {
      color: #6c757d;
    }
    
    h6 {
      color: #495057;
      margin-bottom: 0.5rem;
    }
    
    p {
      margin-bottom: 0;
    }
  }
}
</style>